import React, { useRef, useState, useEffect } from 'react';
import CircleButton from 'components/v2/buttons/CircleButton';
import CheckIcon from 'components/v2/icons/CheckIcon';
import { sluggify } from 'utils/localization/utils';
import { ACCEPTED_FILE_TYPES } from 'config/accepted-file-types';
import PropTypes from 'prop-types';
import { useHandleUploadDocumentsByForeignKeys } from 'utils/DocumentsHooks';
import { getSignedUrl, waitDocumentUpload } from 'utils/DocumentsAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import { CargomaticDropzone } from 'components/v2/Dropzone';
import { t } from 'i18next';
import { featureEnabled, CONFIGURED_FEATURES } from 'helpers/feature-flags';
import { FEATURE_FLAG_ENABLED_BREADCRUMB } from 'constants/temp-feature-flag-breadcrumb';
import { accessorialRequestDocsWhitelist } from 'config/documents';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import Alert from 'components/ui/Alert';

const OTHER = 'other';

/*
  This list has been set up to either use a built-in document uploader, or handle document uploads through the custom handleDocumentUpload prop function
*/

const RequiredAccessorialDocumentList = (props) => {
  const {
    currentDocs,
    requiredDocs,
    shipment,
    accessorial,
    onUpload,
    uploadedFiles,
    onUploadFailed,
    handleDocumentUpload,
    handleDocumentUploading,
    dataTestId,
    isExistingAccessorialRequest,
  } = props;
  const dropzoneRef = useRef(null);

  const shipmentId = shipment?._id || shipment;
  let accessorialRequestId;
  let foreignKeysForDocumentAttachment;
  let { accessorialTypeId } = props;
  if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
    accessorialRequestId = isExistingAccessorialRequest
      ? accessorial?._id || accessorial
      : null;
    accessorialTypeId = isExistingAccessorialRequest
      ? accessorialTypeId
      : accessorial?._id || accessorial;
    foreignKeysForDocumentAttachment = [
      shipmentId,
      accessorialTypeId,
      ...(accessorialRequestId ? [accessorialRequestId] : []),
    ]; // accessorial request ID only exists if this component is rendered for an existing accessorial request
  } else {
    accessorialRequestId = accessorial?._id || accessorial;
    foreignKeysForDocumentAttachment = [shipmentId, accessorialRequestId];
  }

  const isFilePreviouslyUploaded = (documentType) => {
    if (!featureEnabled(CONFIGURED_FEATURES.SSR)) {
      /* Remove this path when SSR goes live */
      return uploadedFiles.find(
        (f) =>
          f.type === documentType &&
          f.foreignKeys.includes(accessorialRequestId.toString()),
      );
    }
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      return uploadedFiles.find(
        (file) =>
          file.foreignKeys.includes(accessorialTypeId) &&
          file.type === documentType,
      );
    }
    return uploadedFiles.find((f) => f.type === documentType);
  };

  const isFileTypeAlreadyInDatabase = (fileType) => {
    if (!featureEnabled(CONFIGURED_FEATURES.SSR)) {
      /* Remove this path when SSR goes live */
      return currentDocs.find(
        (f) =>
          f.type === fileType &&
          f.foreignKeys.includes(accessorialRequestId.toString()),
      );
    }
    if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
      const foundDoc = currentDocs.find((doc) => {
        if (accessorialRequestDocsWhitelist[doc.type]) {
          return doc.type === fileType;
        }
        return (
          doc.type === fileType && doc.foreignKeys.includes(accessorialTypeId)
        );
      });
      return foundDoc;
    } else {
      // ssr is on but carrier accessorial docs is off
      return currentDocs.find((f) => f.type === fileType);
    }
  };
  const renderNameOrHyperLink = (doc) => {
    const needsHyperLink = isFileTypeAlreadyInDatabase(doc.value);
    const [documentLink, setDocumentLink] = useState();

    const getAndSetDocumentLink = async (type) => {
      let filteredArray;

      if (FEATURE_FLAG_ENABLED_BREADCRUMB) {
        filteredArray = currentDocs.filter((currentDoc) => {
          if (accessorialRequestDocsWhitelist[currentDoc.type]) {
            return currentDoc.type === type;
          }
          return (
            currentDoc.type === type &&
            currentDoc.foreignKeys.includes(accessorialTypeId)
          );
        });
      } else {
        filteredArray = currentDocs.filter((f) => f.type === type) ?? [];
      }
      if (!filteredArray?.length) return setDocumentLink(null);

      const sortedArray = filteredArray.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );

      const document = sortedArray[0];

      const signedUrlResponse = await getSignedUrl(document.key),
        signedUrl = signedUrlResponse.data?.getSignedUrl.signedUrl ?? '';
      setDocumentLink(signedUrl);
    };

    useEffect(() => {
      if (needsHyperLink) getAndSetDocumentLink(doc.value);
    }, [needsHyperLink]);

    const docText = FEATURE_FLAG_ENABLED_BREADCRUMB
      ? t(sluggify(doc.slug ?? doc.value))
      : t(sluggify(doc.value));
    return needsHyperLink ? (
      <TableCell
        data-testid="td-8021df97"
        id={`accessorial_${doc.value}_requirement_name`}
      >
        <a data-testid="a-4b3365ef" href={documentLink} target="blank">
          {docText}
        </a>
      </TableCell>
    ) : (
      <TableCell
        data-testid="td-45b03be7"
        id={`accessorial_${doc.value}_requirement_name`}
      >
        {docText}
      </TableCell>
    );
  };

  const renderRowIcon = (doc) => {
    const { uploading, handleUpload } = useHandleUploadDocumentsByForeignKeys(
      foreignKeysForDocumentAttachment,
      onUpload,
      onUploadFailed,
    );

    const onDocumentUpload = async (files) => {
      const docType = doc?.value;
      try {
        handleDocumentUploading(true);
        let documentResult;
        if (handleDocumentUpload) {
          documentResult = await handleDocumentUpload(
            files,
            docType,
            dropzoneRef,
          );
        } else {
          documentResult = await uploadDocument(files, doc);
        }
        handleDocumentUploading(false);
        return documentResult;
      } catch (e) {
        handleDocumentUploading(false);
        if (onUploadFailed) onUploadFailed(e);
        console.log('error in document upload: ', e);
      }
    };

    const uploadDocument = async (files, doc) => {
      const docType = doc?.value;
      const docName = doc?.slug;
      const documentName = docType === OTHER ? docName : docType;
      dropzoneRef.current?.open();
      let documentResult;
      documentResult = await handleUpload(
        files.map((file) => {
          return new File([file], documentName, {
            ...file.options,
            type: file.type,
          });
        }),
        docType,
        undefined,
        true,
      );
      if (documentResult?.data) {
        documentResult = await waitDocumentUpload(documentResult.data.key);
      }
      return documentResult;
    };

    const isProcessing = () => {
      return !!uploading;
    };

    if (uploading) {
      return (
        <TableCell data-testid="td-72b5b663">
          <LoadingCircle
            dataTestId="LoadingCircle-d32974e8"
            text={t('PROCESSING_FILES')}
          />
        </TableCell>
      );
    }

    const emptyTableCell = <TableCell></TableCell>;

    const dropZoneTD = (
      <TableCell data-testid="td-7946bedc">
        <CargomaticDropzone
          dataTestId="CargomaticDropzone-600cad72"
          onDrop={(files) => {
            onDocumentUpload(files, doc);
          }}
          style={{ padding: 0, borderWidth: 0, borderRadius: 0 }}
          ref={dropzoneRef}
          multiple={false}
          acceptedFiles={ACCEPTED_FILE_TYPES}
          disabled={isProcessing()}
          dropzoneMessage=""
          button
        />
      </TableCell>
    );

    if (
      !doc.file &&
      !isFilePreviouslyUploaded(doc.value) &&
      !isFileTypeAlreadyInDatabase(doc.value)
    ) {
      return (
        <>
          {emptyTableCell}
          {dropZoneTD}
        </>
      );
    }
    return (
      <>
        <TableCell data-testid="td-e0675c1a">
          <CircleButton dataTestId="CircleButton-24dd744c" color="green">
            <CheckIcon className="w-5 h-5" />
          </CircleButton>
        </TableCell>
        {FEATURE_FLAG_ENABLED_BREADCRUMB && dropZoneTD}
      </>
    );
  };

  if (requiredDocs.length === 0) {
    return (
      <Alert type="success" className="my-4">
        There are no required documents
      </Alert>
    );
  }

  return (
    <div data-testid={dataTestId}>
      <Table data-testid="table-255bef2c">
        <TableHeader data-testid="thead-0bb65594">
          <TableRow data-testid="tr-9b94f709">
            <TableHead data-testid="th-bad9c5d6">
              {t('DOCUMENT_TYPE')}
            </TableHead>
            <TableHead data-testid="th-d0a73a9c">&nbsp;</TableHead>
            {FEATURE_FLAG_ENABLED_BREADCRUMB && (
              <TableHead data-testid="th-d0a73a9c">&nbsp;</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody data-testid="tbody-1b8459ad">
          {requiredDocs.map((doc, index) => (
            <TableRow
              key={doc.value}
              data-testid={`tr-557ee181-${index}`}
              id={`accessorial_${doc.value}_requirement`}
            >
              {renderNameOrHyperLink(doc)}
              {renderRowIcon(doc)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RequiredAccessorialDocumentList;

RequiredAccessorialDocumentList.propTypes = {
  currentDocs: PropTypes.array,
  requiredDocs: PropTypes.array,
  shipment: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  onUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array,
  onUploadFailed: PropTypes.func,
  handleDocumentUploading: PropTypes.func,
  handleDocumentUpload: PropTypes.func,
  isExistingAccessorialRequest: PropTypes.bool,
  accessorialTypeId: PropTypes.string,
};

RequiredAccessorialDocumentList.defaultProps = {
  currentDocs: [],
  requiredDocs: [],
  uploadedFiles: [],
  handleDocumentUpload: null,
  isExistingAccessorialRequest: false,
  accessorialTypeId: null,
};
